import React, { createContext, useContext, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuthRefresh } from '../lib/useAuthRefresh';

// Harmony Candor API context. Same isolation rule as Pulse: useAuthRefresh
// only touches calls whose URL contains '/api/candor'.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/candor`;

const CandorContext = createContext(null);

export const useCandor = () => {
  const ctx = useContext(CandorContext);
  if (!ctx) throw new Error('useCandor must be used inside CandorProvider');
  return ctx;
};

export const CandorProvider = ({ children, token, userName, role }) => {
  const { tokenRef } = useAuthRefresh(token, { backendUrl: BACKEND_URL, apiPrefix: '/api/candor' });

  const cfg = useCallback(
    () => ({ headers: { Authorization: `Bearer ${tokenRef.current}` } }),
    [tokenRef],
  );

  const api = useMemo(() => ({
    me:            ()          => axios.get(`${API}/me`, cfg()).then((r) => r.data),
    listSessions:  ()          => axios.get(`${API}/sessions`, cfg()).then((r) => r.data),
    getSession:    (id)        => axios.get(`${API}/sessions/${id}`, cfg()).then((r) => r.data),
    createSession: (body)      => axios.post(`${API}/sessions`, body, cfg()).then((r) => r.data),
    endSession:    (id)        => axios.post(`${API}/sessions/${id}/end`, {}, cfg()).then((r) => r.data),
    deleteSession: (id)        => axios.delete(`${API}/sessions/${id}`, cfg()).then((r) => r.data),
    setNotes:      (id, notes) => axios.put(`${API}/sessions/${id}/notes`, { notes }, cfg()).then((r) => r.data),
    getSignatures:   ()        => axios.get(`${API}/signatures`, cfg()).then((r) => r.data),
    saveSignatures:  (lists)   => axios.put(`${API}/signatures`, { lists }, cfg()).then((r) => r.data),
    resetSignatures: ()        => axios.post(`${API}/signatures/reset`, {}, cfg()).then((r) => r.data),
  }), [cfg]);

  const value = useMemo(() => ({ api, userName, role }), [api, userName, role]);
  return <CandorContext.Provider value={value}>{children}</CandorContext.Provider>;
};

export const errMsg = (e, fallback = 'Something went wrong') =>
  e?.response?.data?.detail || e?.message || fallback;
